import { useCallback, forwardRef } from "react";
import { Link as RouterLink, useNavigate as useRouterNavigate, useLocation, matchPath, type LinkProps, type NavLinkProps, type NavigateFunction, type NavigateOptions, type To } from "react-router-dom";
import { useViewportLayoutMode } from "./useViewportLayoutMode.js";
import { modeForViewport, type LayoutMode } from "./layoutMode.js";
import { pathForMode, toMobilePath } from "./widePath.js";

/** Normalize only application paths; relative links and external URLs retain router semantics. */
export function destinationForMode(to: To, mode: LayoutMode): To {
  if (typeof to === "string") {
    if (!to.startsWith("/") || to.startsWith("//")) return to;
    const index = to.search(/[?#]/);
    const path = index < 0 ? to : to.slice(0, index);
    return pathForMode(path, mode) + (index < 0 ? "" : to.slice(index));
  }
  return to.pathname?.startsWith("/") ? { ...to, pathname: pathForMode(to.pathname, mode) } : to;
}

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link({ to, ...props }, ref) {
  const mode = useViewportLayoutMode();
  return <RouterLink {...props} ref={ref} to={destinationForMode(to, mode)} />;
});
export const NavLink = forwardRef<HTMLAnchorElement, NavLinkProps>(function NavLink({ to, end, caseSensitive, className, style, children, ...props }, ref) {
  const mode = useViewportLayoutMode();
  const location = useLocation();
  const path = typeof to === "string" ? to.split(/[?#]/)[0]! : to.pathname ?? location.pathname;
  const canonical = toMobilePath(path);
  const isActive = matchPath({ path: canonical, end: end ?? canonical === "/", caseSensitive }, toMobilePath(location.pathname)) !== null;
  const state = { isActive, isPending: false, isTransitioning: false };
  return <RouterLink {...props} ref={ref} to={destinationForMode(to, mode)} aria-current={isActive ? "page" : undefined}
    className={typeof className === "function" ? className(state) : className}
    style={typeof style === "function" ? style(state) : style}>
    {typeof children === "function" ? children(state) : children}
  </RouterLink>;
});
export function useNavigate(): NavigateFunction {
  const navigate = useRouterNavigate();
  return useCallback(((to: To | number, options?: NavigateOptions) => {
    if (typeof to === "number") return navigate(to);
    return navigate(destinationForMode(to, modeForViewport(window.innerWidth, window.innerHeight)), options);
  }) as NavigateFunction, [navigate]);
}
